import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

/**
 * BuildPilotOpinion, canonical editorial callout used to separate
 * BuildPilot's own editorial reading from cited primary sources.
 *
 * Two variants:
 *   - "gradient" (default): filled brand-blue gradient block, white text.
 *     Use as a strong editorial moment on a light page. Signed off on
 *     /sources.
 *   - "subtle": white card with a brand-blue eyebrow + 3px gradient left
 *     bar. Use inside another card, or below a resource block, when a
 *     second gradient fill would clash visually.
 *
 * Cross-link rule:
 *   Every opinion block links back to /sources, the public index of the
 *   authorities BuildPilot reads from. This closes the legal-compliance
 *   loop visibly on every editorial moment: opinion <-> sources <-> facts.
 *
 * Legal posture:
 *   Label defaults to "BuildPilot opinion" so the editorial line between
 *   fact and opinion is explicit. Never relabel to "advice". Real advice
 *   lives behind One on One Support.
 */
const BuildPilotOpinion = ({
  children,
  variant = 'gradient',
  label = 'BuildPilot opinion',
  className = '',
  testId = 'buildpilot-opinion',
  showSourcesLink = true,
  sourcesHref = '/sources',
  sourcesLabel = 'Read our sources',
}) => {
  if (variant === 'subtle') {
    return (
      <aside
        data-testid={testId}
        className={`relative bg-white rounded-r-xl pl-5 pr-5 py-5 sm:pl-6 sm:pr-6 sm:py-6 my-6 ${className}`}
        style={{
          borderLeft: '3px solid transparent',
          backgroundImage:
            'linear-gradient(white, white), linear-gradient(180deg, #0a2540 0%, #1d8efe 50%, #4abff2 100%)',
          backgroundOrigin: 'border-box',
          backgroundClip: 'padding-box, border-box',
        }}
      >
        <p
          className="text-[10.5px] sm:text-[11px] font-semibold uppercase tracking-[0.04em] text-[#1d8efe] mb-2"
          data-testid={`${testId}-eyebrow`}
        >
          {label}
        </p>
        <div className="text-[14px] sm:text-[15px] text-[#1d1d1f] leading-[1.6]">
          {typeof children === 'string' ? <p>{children}</p> : children}
        </div>
        {showSourcesLink && (
          <Link
            to={sourcesHref}
            className="mt-3 inline-flex items-center gap-1 text-[11.5px] font-semibold text-[#1d8efe] hover:text-[#1670cc] uppercase tracking-[0.06em]"
            data-testid={`${testId}-sources-link`}
          >
            {sourcesLabel} <ArrowRight className="w-3 h-3" />
          </Link>
        )}
      </aside>
    );
  }

  // gradient (default)
  return (
    <div
      className={`p-4 sm:p-5 bg-[#1d8efe] rounded-lg shadow-md ${className}`}
      data-testid={testId}
    >
      <p
        className="text-[10.5px] font-semibold uppercase tracking-[0.07em] text-white/75 mb-2"
        data-testid={`${testId}-eyebrow`}
      >
        {label}
      </p>
      <div className="text-[13px] sm:text-[13.5px] text-white leading-relaxed [&_a]:underline [&_a]:underline-offset-2 [&_a:hover]:text-white/90">
        {typeof children === 'string' ? <p>{children}</p> : children}
      </div>
      {showSourcesLink && (
        <Link
          to={sourcesHref}
          className="mt-3 inline-flex items-center gap-1 text-[11px] font-semibold text-white/90 hover:text-white uppercase tracking-[0.06em]"
          data-testid={`${testId}-sources-link`}
        >
          {sourcesLabel} <ArrowRight className="w-3 h-3" />
        </Link>
      )}
    </div>
  );
};

export default BuildPilotOpinion;
